// Shared color-grouping utility for Maneuvers children (Map, Scatter, TimeSeries, Tables)
// Builds a color scale and groups data by the current color dimension
import * as d3 from 'd3';
import { EMPTY_GROUP_COLOR, getColorByIndex, isEmptyGroupValue } from './colorScale';
import { sourcesStore } from '../store/sourcesStore';
import { warn as logWarning, debug as logDebug } from './console';

type Group<T> = { key: string | number; items: T[] };

function extractValue(item: any, colorName: string): number | string {
  switch (colorName) {
    case 'TWS':
      return item.tws_bin;
    case 'VMG':
      return item.vmg_perc_avg;
    case 'TACK':
      return item.tack;
    case 'RACE':
      // Use normalized field name first (unifiedDataStore normalizes metadata)
      const raceValue = item.race_number ?? item.race ?? item.Race_number ?? item.Race ?? item.RACE;
      if (raceValue === -1 || raceValue === '-1' || String(raceValue) === '-1') {
        return 'TRAINING';
      }
      return raceValue;
    case 'SOURCE':
      return item.source_name ?? '';
    case 'STATE':
      // Use normalized field name first
      return item.state ?? item.State ?? item.STATE ?? '';
    case 'CONFIG':
      return item.config ?? item.Config ?? item.CONFIG ?? '';
    case 'CREW':
      return item.crew ?? item.Crew ?? item.CREW ?? item.CREW_CODE ?? '';
    case 'TESTS':
      return item.tests ?? item.Tests ?? item.TESTS ?? item.TEST ?? 'NONE';
    case 'YEAR':
      return item.year ?? item.Year ?? item.YEAR ?? '';
    case 'EVENT':
      return item.event ?? item.Event ?? item.EVENT ?? item.event_type ?? '';
    default:
      return '';
  }
}

/** Returns the group key for an item under the given color dimension (same rules as extractValue, including RACE -1 → 'TRAINING'). */
export function getGroupKeyFromItem(item: any, colorName: string): string | number {
  const v = extractValue(item, colorName);
  return v === undefined || v === null ? '' : v;
}

/**
 * Converts legend display text (as passed from a color legend click) to the same key format
 * used by getGroupKeyFromItem, so legend clicks can toggle selectedGroupKeys correctly.
 */
export function legendTextToGroupKey(legendItem: string, colorName: string): string | number {
  const s = String(legendItem).trim();
  if (s === '') return '';
  switch (colorName) {
    case 'TWS': {
      const n = Number(s);
      return isNaN(n) ? s : n;
    }
    case 'VMG':
      // VMG legend shows intervals like "1.2-3.4"; keep as string
      return s;
    case 'TACK':
      return s;
    case 'RACE':
      if (s === '-1' || s.toLowerCase() === 'training') return 'TRAINING';
      const rn = Number(s);
      return isNaN(rn) ? s : rn;
    case 'SOURCE':
      return s.toLowerCase();
    case 'STATE':
    case 'CONFIG':
    case 'CREW':
    case 'TESTS':
    case 'YEAR':
    case 'EVENT':
    default:
      return s;
  }
}

/**
 * Converts legend display text to the exact key format used by getGroupKeyFromItem (e.g. in the grouped DataTable).
 * Use this for legend clicks so selection matches the table: same key format, same includes() check.
 * Differs from legendTextToGroupKey for SOURCE (no lowercase) so keys match table row keys.
 */
export function legendTextToGroupKeyTable(legendItem: string, colorName: string): string | number {
  const s = String(legendItem).trim();
  if (s === '') return '';
  switch (colorName) {
    case 'TWS': {
      const n = Number(s);
      return isNaN(n) ? s : n;
    }
    case 'VMG':
      return s;
    case 'TACK':
      return s;
    case 'RACE':
      if (s === '-1' || s.toLowerCase() === 'training') return 'TRAINING';
      const rn = Number(s);
      return isNaN(rn) ? s : rn;
    case 'SOURCE':
    case 'STATE':
    case 'CONFIG':
    case 'CREW':
    case 'TESTS':
    case 'YEAR':
    case 'EVENT':
    default:
      return s;
  }
}

/**
 * Group key for a maneuver track from its first point, matching grouped TimeSeries / legend / map.
 * VMG color mode groups by `tws_bin` (same as TimeSeries `groupByKeyName`), not `vmg_perc_avg`.
 */
export function getManeuverGroupKeyFromFirstPoint(
  firstPoint: Record<string, unknown> | null | undefined,
  colorMode: string
): string | number {
  const mode = String(colorMode || '').toUpperCase();
  if (mode === 'VMG') {
    const v = firstPoint?.tws_bin as number | undefined;
    return v === undefined || v === null ? '' : v;
  }
  if (!firstPoint) return '';
  return getGroupKeyFromItem(firstPoint as Record<string, unknown>, colorMode);
}

/**
 * Primary property name written on grouped / averaged time-series rows for the active color dimension.
 * VMG uses `tws_bin` (same as getManeuverGroupKeyFromFirstPoint / legacy groupByKeyName), not `vmg_perc_avg`.
 */
export function maneuverGroupByField(colorName: string): string {
  const u = String(colorName || "").toUpperCase();
  switch (u) {
    case "TWS":
      return "tws_bin";
    case "VMG":
      return "tws_bin";
    case "TACK":
      return "tack";
    case "RACE":
      return "race";
    case "SOURCE":
      return "source_name";
    case "STATE":
      return "State";
    case "CONFIG":
      return "Config";
    case "CREW":
      return "Crew";
    case "TESTS":
      return "Tests";
    case "YEAR":
      return "Year";
    case "EVENT":
      return "event";
    default:
      return "tws_bin";
  }
}

/**
 * Group key for grouped time-series averaging (per sample), aligned with getManeuverGroupKeyFromFirstPoint:
 * VMG color mode buckets by `tws_bin`, not `vmg_perc_avg`.
 */
export function getTimeSeriesGroupKeyFromPoint(
  pt: Record<string, unknown>,
  colorMode: string
): string | number {
  const u = String(colorMode || "").toUpperCase();
  if (u === "VMG") {
    return getManeuverGroupKeyFromFirstPoint(pt, "VMG");
  }
  return getGroupKeyFromItem(pt, colorMode);
}

/** Returns true if two group keys are equivalent (e.g. 15 and "15" for TWS). */
export function groupKeyEquals(a: string | number, b: string | number): boolean {
  if (a === b) return true;
  if (String(a) === String(b)) return true;
  const na = Number(a);
  const nb = Number(b);
  if (!isNaN(na) && !isNaN(nb) && na === nb) return true;
  return false;
}

/** Toggle whether `key` is in `prev`, using groupKeyEquals so numeric and string keys match. */
export function toggleGroupKeyInList(
  prev: readonly (string | number)[],
  key: string | number
): (string | number)[] {
  const idx = prev.findIndex((k) => groupKeyEquals(k, key));
  if (idx >= 0) {
    const next = [...prev];
    next.splice(idx, 1);
    return next;
  }
  return [...prev, key];
}

/** True if `list` contains an entry equal to `key` per groupKeyEquals. */
export function listIncludesGroupKey(
  list: readonly (string | number)[],
  key: string | number
): boolean {
  return list.some((k) => groupKeyEquals(k, key));
}

export function buildColorGrouping<T extends Record<string, any>>(data: T[], colorName: string) {
  const values = (data || []).map(d => extractValue(d, colorName)).filter(v => v !== undefined && v !== null);

  let scale: d3.ScaleLinear<number, string> | d3.ScaleThreshold<number, string> | d3.ScaleOrdinal<string | number, string>;

  if (colorName === 'TWS') {
    // Fixed TWS bins: 10, 15, 20, 25, 30, 35, 40, 45, 50
    // Use scaleThreshold with 8 thresholds to create 9 color ranges (one for each bin)
    const twsScale = d3.scaleThreshold<number, string>();
    twsScale.domain([12.5, 17.5, 22.5, 27.5, 32.5, 37.5, 42.5, 47.5]);
    twsScale.range(["blue","lightblue","cyan","lightgreen","yellow","orange","red","darkred","purple"]);
    scale = twsScale;
  } else if (colorName === 'VMG') {
    const nums: number[] = values.map(v => Number(v)).filter((v): v is number => !isNaN(v) && isFinite(v));
    if (nums.length === 0) {
      const vmgScale = d3.scaleLinear<number, string>();
      vmgScale.domain([0, 50, 100]);
      (vmgScale as any).range(["blue","lightgrey","red"]);
      scale = vmgScale;
    } else {
      // Use 1-sigma range (mean ± 1 std) for VMG coloring
      const mean = d3.mean(nums) || 0;
      const std = d3.deviation(nums) || 0;
      const min = mean - std;
      const max = mean + std;
      const mid = (min + max) / 2;
      const vmgScale = d3.scaleLinear<number, string>();
      vmgScale.domain([min, mid, max]);
      (vmgScale as any).range(["blue","lightgrey","red"]);
      scale = vmgScale;
    }
  } else if (colorName === 'TACK') {
    scale = d3.scaleThreshold<number, string>().domain([-180,-1,1,180]).range(["red","red","#64ed64","#64ed64"]);
  } else if (colorName === 'SOURCE') {
    // Use fleet source colors from store (matching FleetPerformance approach)
    const sources = sourcesStore.sources();
    const isReady = sourcesStore.isReady();
    
    if (sources.length > 0) {
      // Use source names (lowercase) as domain and their colors as range
      // This matches how FleetPerformance creates its color scale
      const sourceNames = sources.map(s => String(s.source_name).toLowerCase());
      const colors = sources.map(s => s.color || '#1f77b4');
      
      const ordinalScale = d3.scaleOrdinal<string | number, string>()
        .domain(sourceNames)
        .range(colors);
      (ordinalScale as any).unknown('#1f77b4');
      scale = ordinalScale;
    } else if (!isReady) {
      // Sources are empty and store is not ready - trigger refresh
      // This happens when app loads fresh and sources haven't been fetched yet
      logDebug('buildColorGrouping: Sources empty and store not ready, triggering refresh');
      sourcesStore.refresh().catch((error) => {
        logWarning('buildColorGrouping: Failed to refresh sources', error);
      });
      
      // Fallback to index-based colors while sources are loading
      const cats = Array.from(new Set(values as (string | number)[]));
      const fallbackColors: string[] = [];
      let i = 0;
      cats.forEach(() => {
        fallbackColors.push(getColorByIndex(i));
        i += 1;
      });
      scale = d3.scaleOrdinal<string | number, string>().domain(cats).range(fallbackColors);
    } else {
      // Sources are empty but store is ready - this means there are no sources for this project
      // Use fallback colors
      logDebug('buildColorGrouping: Sources empty but store is ready - no sources for this project');
      const cats = Array.from(new Set(values as (string | number)[]));
      const fallbackColors: string[] = [];
      let i = 0;
      cats.forEach(() => {
        fallbackColors.push(getColorByIndex(i));
        i += 1;
      });
      scale = d3.scaleOrdinal<string | number, string>().domain(cats).range(fallbackColors);
    }
  } else if (colorName === 'MAINSAIL' || colorName === 'HEADSAIL') {
    // Custom color mapping for sail codes: J1=blue, J1.5=lightblue, J2=green, J3=orange, J4=red
    const sailColorMap: Record<string, string> = {
      'J1': 'blue',
      'j1': 'blue',
      'J1.5': 'lightblue',
      'j1.5': 'lightblue',
      'J2': 'green',
      'j2': 'green',
      'J3': 'orange',
      'j3': 'orange',
      'J4': 'red',
      'j4': 'red'
    };
    
    const cats = Array.from(new Set(values as (string | number)[]));
    const colors: string[] = [];
    cats.forEach(v => {
      const sailCode = String(v);
      // Check if we have a custom color for this sail code
      if (sailColorMap[sailCode]) {
        colors.push(sailColorMap[sailCode]);
      } else {
        // Fallback to index-based color for unknown sail codes
        const index = cats.indexOf(v);
        colors.push(getColorByIndex(index));
      }
    });
    scale = d3.scaleOrdinal<string | number, string>().domain(cats).range(colors);
  } else if (colorName === 'RACE') {
    const cats = Array.from(new Set(values as (string | number)[]));
    // Sort RACE values consistently to ensure same colors across all components
    // Handle 'TRAINING' and numeric values specially
    const sortedCats = cats.sort((a, b) => {
      const aStr = String(a);
      const bStr = String(b);
      // Put 'TRAINING' first, then sort others
      if (aStr === 'TRAINING' || aStr === '-1') return -1;
      if (bStr === 'TRAINING' || bStr === '-1') return 1;
      // Sort numeric values numerically, strings alphabetically
      const aNum = Number(a);
      const bNum = Number(b);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum - bNum;
      }
      return aStr.localeCompare(bStr);
    });
    const colors: string[] = [];
    let i = 0;
    sortedCats.forEach(v => {
      if (isEmptyGroupValue(v) || String(v) === '0' || String(v) === 'TRAINING' || String(v) === '-1') {
        colors.push(EMPTY_GROUP_COLOR);
      } else {
        colors.push(getColorByIndex(i));
      }
      i += 1;
    });
    scale = d3.scaleOrdinal<string | number, string>().domain(sortedCats).range(colors);
  } else if (colorName === 'STATE') {
    const cats = Array.from(new Set(values as (string | number)[]));
    // Sort State values consistently to ensure same colors across all components
    const sortedCats = cats.sort((a, b) => {
      const aStr = String(a);
      const bStr = String(b);
      return aStr.localeCompare(bStr);
    });
    const colors: string[] = [];
    let i = 0;
    sortedCats.forEach((v) => {
      colors.push(isEmptyGroupValue(v) ? EMPTY_GROUP_COLOR : getColorByIndex(i));
      i += 1;
    });
    scale = d3.scaleOrdinal<string | number, string>().domain(sortedCats).range(colors);
  } else if (colorName === 'CONFIG' || colorName === 'CREW' || colorName === 'TESTS') {
    const cats = Array.from(new Set(values as (string | number)[]));
    // Sort categorical values consistently to ensure same colors across all components
    const sortedCats = cats.sort((a, b) => {
      const aStr = String(a);
      const bStr = String(b);
      return aStr.localeCompare(bStr);
    });
    const colors: string[] = [];
    let i = 0;
    sortedCats.forEach((v) => {
      colors.push(isEmptyGroupValue(v) ? EMPTY_GROUP_COLOR : getColorByIndex(i));
      i += 1;
    });
    scale = d3.scaleOrdinal<string | number, string>().domain(sortedCats).range(colors);
  } else if (colorName === 'YEAR') {
    const cats = Array.from(new Set(values as (string | number)[]));
    const sortedCats = cats.sort((a, b) => String(a).localeCompare(String(b)));
    const colors: string[] = [];
    let i = 0;
    sortedCats.forEach((v) => {
      colors.push(isEmptyGroupValue(v) ? EMPTY_GROUP_COLOR : getColorByIndex(i));
      i += 1;
    });
    scale = d3.scaleOrdinal<string | number, string>().domain(sortedCats).range(colors);
  } else if (colorName === 'EVENT') {
    const cats = Array.from(new Set(values as (string | number)[]));
    const sortedCats = cats.sort((a, b) => String(a).localeCompare(String(b)));
    const colors: string[] = [];
    let i = 0;
    sortedCats.forEach((v) => {
      colors.push(isEmptyGroupValue(v) ? EMPTY_GROUP_COLOR : getColorByIndex(i));
      i += 1;
    });
    scale = d3.scaleOrdinal<string | number, string>().domain(sortedCats).range(colors);
  } else {
    // Default fallback
    const defaultScale = d3.scaleLinear<number, string>();
    // defaultScale.domain([4, 8, 14, 18, 22]);
    defaultScale.domain([8, 16, 28, 36, 44]);
    (defaultScale as any).range(["yellow","orange","red"]);
    scale = defaultScale;
  }

  const groups = new Map<string | number, T[]>();
  (data || []).forEach(item => {
    const k = extractValue(item, colorName);
    const key = (k === undefined || k === null) ? '' : (k as any);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(item);
  });

  const grouped: Group<T>[] = Array.from(groups.entries()).map(([key, items]) => ({ key, items }));

  const getItemColor = (item: T): string => {
    if (colorName === 'TACK') {
      // TACK handled by thresholds
      const v = extractValue(item, colorName);
      if (v === 'PORT' || v === 'P - S') return '#d62728';
      if (v === 'STBD' || v === 'S - P') return '#2ca02c';
      return 'grey';
    }
    const v = extractValue(item, colorName) as any;
    // d3.scaleOrdinal gives an unknown value the next range color. With only a NONE
    // bucket that color is category blue, while the legend swatch is light grey.
    if (
      colorName !== 'SOURCE' &&
      colorName !== 'TWS' &&
      colorName !== 'VMG' &&
      isEmptyGroupValue(v)
    ) {
      return EMPTY_GROUP_COLOR;
    }
    // For SOURCE, normalize to lowercase for lookup
    if (colorName === 'SOURCE' && typeof v === 'string') {
      // @ts-ignore
      return (scale as any)(v.toLowerCase());
    }
    // @ts-ignore
    return (scale as any)(v);
  };

  return { scale, groups: grouped, getItemColor };
}

export interface VmgIntervalCalculator {
  min: number;
  max: number;
  intervalSize: number;
  getInterval: (vmg: number) => string | null;
}

/** Five equal VMG intervals from min to max (same logic as maneuver tables / legend). */
export function calculateVmgIntervals(rows: readonly Record<string, unknown>[]): VmgIntervalCalculator | null {
  const vmgValues = rows
    .map((r) => parseFloat(String(r.vmg_perc_avg ?? '')))
    .filter((v) => !isNaN(v) && v !== null && v !== undefined);

  if (vmgValues.length === 0) return null;

  const min = Math.min(...vmgValues);
  const max = Math.max(...vmgValues);

  if (min === max) {
    return {
      min,
      max,
      intervalSize: 0,
      getInterval: () => `${min.toFixed(1)}-${max.toFixed(1)}`,
    };
  }

  const intervalSize = (max - min) / 5;

  return {
    min,
    max,
    intervalSize,
    getInterval: (vmg: number) => {
      if (vmg < min || vmg > max) return null;
      const intervalIndex = Math.min(4, Math.floor((vmg - min) / intervalSize));
      const intervalMin = min + intervalIndex * intervalSize;
      const intervalMax = intervalIndex === 4 ? max : min + (intervalIndex + 1) * intervalSize;
      return `${intervalMin.toFixed(1)}-${intervalMax.toFixed(1)}`;
    },
  };
}

function vmgIntervalLabelsMatch(calculatedInterval: string, clickedItem: string): boolean {
  const calculatedMatch = calculatedInterval.match(/^([\d.]+)-([\d.]+)$/);
  const clickedMatch = clickedItem.match(/^([\d.]+)-([\d.]+)$/);
  if (!calculatedMatch || !clickedMatch) return false;
  const calcMin = parseFloat(calculatedMatch[1]);
  const calcMax = parseFloat(calculatedMatch[2]);
  const clickMin = parseFloat(clickedMatch[1]);
  const clickMax = parseFloat(clickedMatch[2]);
  const tolerance = 0.01;
  return Math.abs(calcMin - clickMin) < tolerance && Math.abs(calcMax - clickMax) < tolerance;
}

/**
 * True when `item` belongs to the legend category identified by `legendLabel` for `colorMode`.
 * Use for non-grouped legend selection so legend, table, map, and scatter agree on membership.
 */
export function maneuverMatchesLegendColorItem(
  item: Record<string, unknown>,
  colorMode: string,
  legendLabel: string,
  options?: { vmgIntervals?: VmgIntervalCalculator | null }
): boolean {
  const modeUpper = String(colorMode || 'TWS').toUpperCase();
  const clickedItem = String(legendLabel).trim();
  if (clickedItem === '') return false;

  if (modeUpper === 'VMG') {
    const vmgIntervals = options?.vmgIntervals;
    if (!vmgIntervals) return false;
    const vmgValue = parseFloat(String(item.vmg_perc_avg ?? ''));
    if (isNaN(vmgValue)) return false;
    const calculatedInterval = vmgIntervals.getInterval(vmgValue);
    if (!calculatedInterval) return false;
    return vmgIntervalLabelsMatch(calculatedInterval, clickedItem);
  }

  if (modeUpper === 'TWS') {
    const twsBin = item.tws_bin;
    const twsNum = Number(twsBin);
    const clickedNum = Number(clickedItem);
    if (!isNaN(twsNum) && !isNaN(clickedNum) && twsNum === clickedNum) return true;
    return String(twsBin) === clickedItem;
  }

  if (modeUpper === 'TACK') {
    const tackValue = String(item.tack || '').toUpperCase();
    const clickedTack = clickedItem.toUpperCase();
    if ((tackValue === 'PORT' || tackValue === 'S - P') && (clickedTack === 'PORT' || clickedTack === 'S - P')) {
      return true;
    }
    if ((tackValue === 'STBD' || tackValue === 'P - S') && (clickedTack === 'STBD' || clickedTack === 'P - S')) {
      return true;
    }
    return false;
  }

  if (['RACE', 'SOURCE', 'STATE', 'CONFIG', 'CREW', 'TESTS', 'YEAR', 'EVENT'].includes(modeUpper)) {
    const targetKey = legendTextToGroupKeyTable(clickedItem, modeUpper);
    return groupKeyEquals(getGroupKeyFromItem(item, modeUpper), targetKey);
  }

  const fieldValueStr = item[colorMode.toLowerCase()] ?? item[colorMode];
  if (fieldValueStr === undefined || fieldValueStr === null) return false;
  if (String(fieldValueStr) === clickedItem) return true;
  const fieldNum = Number(fieldValueStr);
  const clickedNum = Number(clickedItem);
  if (!isNaN(fieldNum) && !isNaN(clickedNum) && fieldNum === clickedNum) return true;
  return String(fieldValueStr).toLowerCase() === clickedItem.toLowerCase();
}

/** Toggle one grouped legend category in `selectedGroupKeys` (table key format). */
export function toggleGroupedLegendSelection(
  prev: readonly (string | number)[],
  legendLabel: string,
  colorMode: string
): (string | number)[] {
  const key = legendTextToGroupKeyTable(legendLabel, colorMode);
  return toggleGroupKeyInList(prev, key);
}

/** Event IDs matching a non-grouped legend category click. */
export function collectManeuverIdsForLegendItem(
  items: readonly Record<string, unknown>[],
  colorMode: string,
  legendLabel: string,
  options?: { vmgIntervalSource?: readonly Record<string, unknown>[] }
): number[] {
  const modeUpper = String(colorMode || 'TWS').toUpperCase();
  const vmgIntervals =
    modeUpper === 'VMG' && options?.vmgIntervalSource
      ? calculateVmgIntervals(options.vmgIntervalSource)
      : null;

  return items
    .filter((item) => maneuverMatchesLegendColorItem(item, colorMode, legendLabel, { vmgIntervals }))
    .map((item) => item.event_id as number)
    .filter((id): id is number => id !== undefined && id !== null && !isNaN(Number(id)));
}

/** Returns getItemColor from buildColorGrouping, or null when data/mode is unsafe. */
export function getDimensionItemColorGetter<T extends Record<string, unknown>>(
  data: T[],
  colorMode: string
): ((item: T) => string) | null {
  if (!Array.isArray(data) || data.length === 0 || !colorMode) return null;
  try {
    return buildColorGrouping(data, colorMode).getItemColor;
  } catch {
    return null;
  }
}


